import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  pn,
  ScreenType,
  Stack,
  useParams,
  useUnsavedChanges,
  useValidationErrors
} from "@compass/core";
import {
  Class,
  ClassEditResource,
  Provider,
  ProviderEditResource,
  ProviderEmployment as ProviderEmploymentModel,
  ProviderViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ClassBasicInfo } from "./ClassBasicInfo";
import { ClassPageHeader } from "./ClassPageHeader";

const routeName = "createClass";

class Params {
  screenType: ScreenType = "standard";
}

class OptionalParams {
  closeOnSave?: boolean = false;
}

export const classCreatePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "New Class",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type ClassCreateContextValue = {
  onCreate?: (id: number) => void;
};

export const ClassCreateContext = createContext<ClassCreateContextValue>({} as ClassCreateContextValue);

export const ClassCreatePage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const [classModel, setClassModel] = useState<Class>({} as Class);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [classModel] });
  const navigate = useNavigate();
  const [viewResource, setViewResource] = useState<ProviderViewResource>({});
  const [editResource, setEditResource] = useState<ClassEditResource>({} as ClassEditResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

  useEffect(() => {
    fetch(0);
    fetchViewResource(0);
    fetchEditResource();
  }, []);

  const fetch = (id: number) => {
    ApiService.classes.get(id).then((response) => {
      setClassModel(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.classes.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.classes.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <ClassPageHeader
      handle={classCreatePageRoute.handle}
      class={classModel}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      closeOnSave={closeOnSave}
    />
  );

  const pageBody = (
    <Stack spacing="lg">
      <ClassBasicInfo
        classModel={classModel}
        setClassModel={setClassModel}
        viewResource={viewResource}
        editResource={editResource}
        crudMode={"edit"}
        validationErrors={validationErrors}
      />
    </Stack>
  );
  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      {pageBody}
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      backgroundColor={(theme) => theme.palette.background.default}
      header={pageHeader}
      content={pageBody}
    />
  );
};
